import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useCallback, useState } from "react";
import { useCopyToClipboard } from "react-use";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { useGetCurrentUrl } from "../../../hooks/use-get-current-url";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Button } from "../../buttons/button";
import { Cta } from "../../buttons/cta";
import { JobData } from "../types";

export interface JobDetailCtasProps {
  job: JobData;
  labels: Record<string, string | null>;
}

export const JobDetailCtas: FC<JobDetailCtasProps> = ({ job, labels }) => {
  const { pushEvent } = useDataLayer();
  const [urlCopied, setUrlCopied] = useState(false);
  const [, copy] = useCopyToClipboard();
  const url = useGetCurrentUrl();
  const handleUrlCopy = useCallback(() => {
    if (url) {
      copy(url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  }, [setUrlCopied, copy, url]);

  return (
    <>
      <Cta
        variant="primary"
        label={labels["job-detail-apply"]}
        externalUrl={job.applicationLink}
        pageLink={null}
        onClick={() => {
          pushEvent(
            {
              event: GoogleAnalyticsEvent.JobApplication,
              job: job.jobId,
            },
            ["job"]
          );
        }}
        key={0}
        fullWidth
        size="sm"
      />
      <Button
        variant="primary"
        fullWidth
        type="button"
        size="sm"
        disabled={!!urlCopied}
        onClick={() => {
          handleUrlCopy();
          pushEvent(
            {
              event: GoogleAnalyticsEvent.ShareJobPosition,
              job: job.jobId,
            },
            ["job"]
          );
        }}
        className="grid grid-cols-1 grid-rows-1"
      >
        <div className="col-start-1 row-start-1 m-auto">
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              initial={{
                opacity: 0,
              }}
              exit={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              key={Number(urlCopied)}
            >
              {urlCopied
                ? labels["job-detail-send-to-a-friend-url-copied"]
                : labels["job-detail-send-to-a-friend"]}
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Needed to keep width of button consistent between the two states */}
        <div className="col-start-1 row-start-1 opacity-0">
          {labels["job-detail-send-to-a-friend-url-copied"]}
        </div>
        <div className="col-start-1 row-start-1 opacity-0">
          {labels["job-detail-send-to-a-friend"]}
        </div>
      </Button>
    </>
  );
};
